// src/context/AnonConsentContext.js
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { apiGet, apiPost, apiDelete, bootstrapBffSession } from '../lib/apiClient';

const STORAGE_KEY = 'anonConsentAccepted';

const AnonConsentContext = createContext(null);

export function AnonConsentProvider({ children }) {
  const [consented, setConsented] = useState(false);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await bootstrapBffSession().catch(() => {});
        const stored = localStorage.getItem(STORAGE_KEY);
        const desired = stored === 'true';

        let status;
        try {
          status = await apiGet('/privacy/status');
        } catch (_) {
          status = { optOut: !desired };
        }
        const serverOptOut = !!status?.optOut;

        if (desired) {
          if (serverOptOut) {
            await apiDelete('/privacy/opt-out').catch(() => {});
          }
          if (!cancelled) setConsented(true);
          localStorage.setItem(STORAGE_KEY, 'true');
        } else {
          if (!serverOptOut) {
            await apiPost('/privacy/opt-out').catch(() => {});
          }
          if (!cancelled) setConsented(false);
          localStorage.setItem(STORAGE_KEY, 'false');
        }
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || 'Failed to initialize privacy consent');
          setConsented(false);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const updateConsent = useCallback(async (value, options = {}) => {
    setUpdating(true);
    setError(null);
    try {
      await bootstrapBffSession().catch(() => {});
      const body = options?.recaptchaToken ? { recaptchaToken: options.recaptchaToken } : undefined;
      if (value) {
        await apiDelete('/privacy/opt-out', body);
        localStorage.setItem(STORAGE_KEY, 'true');
      } else {
        await apiPost('/privacy/opt-out', body);
        localStorage.setItem(STORAGE_KEY, 'false');
      }
      setConsented(value);
      return true;
    } catch (e) {
      setError(e?.msg || e?.message || 'Failed to update consent');
      return false;
    } finally {
      setUpdating(false);
    }
  }, []);

  const value = {
    consented,
    loading,
    updating,
    error,
    setConsent: updateConsent,
  };

  return (
    <AnonConsentContext.Provider value={value}>
      {children}
    </AnonConsentContext.Provider>
  );
}

export function useAnonConsent() {
  const ctx = useContext(AnonConsentContext);
  if (!ctx) throw new Error('useAnonConsent must be used within AnonConsentProvider');
  return ctx;
}
